import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Users as UsersIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  listCrews,
  createCrew,
  updateCrew,
  deleteCrew,
  CREW_COLOR_PALETTE,
  DEFAULT_CREW_COLOR,
} from '../services/CrewService';

const Crews = () => {
  const [crews, setCrews] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_CREW_COLOR);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    refresh();
  }, []);

  const refresh = () => {
    setCrews(listCrews());
    try {
      setEmployees(JSON.parse(localStorage.getItem('maidly_employees') || '[]'));
    } catch {
      setEmployees([]);
    }
  };

  const memberCount = (crewId) =>
    employees.filter((e) => e.crewId === crewId && e.status === 'active').length;

  const openCreate = () => {
    setEditing(null);
    setName('');
    setColor(DEFAULT_CREW_COLOR);
    setShowModal(true);
  };

  const openEdit = (crew) => {
    setEditing(crew);
    setName(crew.name);
    setColor(crew.color);
    setShowModal(true);
  };

  const handleSave = () => {
    const cleanName = name.trim();
    if (!cleanName) {
      toast.error('Crew name is required');
      return;
    }
    // Prevent duplicate names (case-insensitive)
    const dup = crews.find(
      (c) =>
        c.name.toLowerCase() === cleanName.toLowerCase() &&
        c.id !== editing?.id
    );
    if (dup) {
      toast.error(`A crew named "${cleanName}" already exists`);
      return;
    }

    if (editing) {
      updateCrew(editing.id, { name: cleanName, color });
      toast.success(`Crew "${cleanName}" updated`);
    } else {
      createCrew({ name: cleanName, color });
      toast.success(`Crew "${cleanName}" created`);
    }
    setShowModal(false);
    refresh();
  };

  const handleDelete = (crew) => {
    const count = memberCount(crew.id);
    const msg =
      count > 0
        ? `Delete crew "${crew.name}"? ${count} employee(s) will be unassigned.`
        : `Delete crew "${crew.name}"?`;
    if (window.confirm(msg)) {
      deleteCrew(crew.id);
      toast.success(`Crew "${crew.name}" deleted`);
      refresh();
    }
  };

  return (
    <div data-testid="crews-page">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
        }}
      >
        <div>
          <h1 style={{ fontSize: '2rem', fontWeight: 700, margin: 0 }}>Crews</h1>
          <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.95rem' }}>
            Group your employees into sub-teams (Crew A, Crew B…) and filter
            scheduling, customers, and reports by crew.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="add-crew-button"
          className="btn btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <Plus size={18} />
          Add Crew
        </button>
      </div>

      {crews.length === 0 ? (
        <div
          className="card"
          style={{
            textAlign: 'center',
            padding: '3rem 2rem',
            border: '2px dashed #e5e7eb',
          }}
          data-testid="crews-empty-state"
        >
          <UsersIcon size={40} color="#9ca3af" style={{ marginBottom: '0.75rem' }} />
          <h3 style={{ margin: 0, color: '#374151' }}>No crews yet</h3>
          <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>
            Create your first crew to start grouping employees.
          </p>
          <button
            onClick={openCreate}
            data-testid="empty-add-crew-button"
            className="btn btn-primary"
            style={{ marginTop: '1.25rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Plus size={18} />
            Add your first crew
          </button>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1rem',
          }}
        >
          {crews.map((crew) => (
            <div
              key={crew.id}
              data-testid={`crew-card-${crew.id}`}
              className="card"
              style={{
                padding: '1.25rem',
                borderLeft: `6px solid ${crew.color}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: crew.color,
                    flexShrink: 0,
                    boxShadow: '0 0 0 2px #fff, 0 0 0 3px #e5e7eb',
                  }}
                />
                <h3
                  style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}
                  data-testid={`crew-name-${crew.id}`}
                >
                  {crew.name}
                </h3>
              </div>
              <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                <strong style={{ color: '#374151' }}>{memberCount(crew.id)}</strong>{' '}
                active member{memberCount(crew.id) === 1 ? '' : 's'}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: 'auto' }}>
                <button
                  onClick={() => openEdit(crew)}
                  data-testid={`edit-crew-${crew.id}`}
                  className="btn btn-secondary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}
                >
                  <Edit size={14} />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(crew)}
                  data-testid={`delete-crew-${crew.id}`}
                  className="btn"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    fontSize: '0.85rem',
                    background: '#fef2f2',
                    color: '#b91c1c',
                    border: '1px solid #fecaca',
                  }}
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowModal(false)}
          data-testid="crew-modal"
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 480 }}
          >
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>
                {editing ? 'Edit Crew' : 'New Crew'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="modal-close"
                data-testid="close-crew-modal"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '1.25rem 1.5rem' }}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label
                  style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}
                >
                  Crew Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Crew A, Morning Team, North Side"
                  className="input"
                  data-testid="crew-name-input"
                  autoFocus
                />
              </div>
              <div>
                <label
                  style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}
                >
                  Color
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {CREW_COLOR_PALETTE.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setColor(c.value)}
                      data-testid={`color-swatch-${c.name.toLowerCase()}`}
                      title={c.name}
                      aria-label={`Pick ${c.name}`}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: c.value,
                        border: color === c.value ? '3px solid #111827' : '3px solid transparent',
                        cursor: 'pointer',
                        outline: 'none',
                        transition: 'transform 0.1s',
                        transform: color === c.value ? 'scale(1.1)' : 'scale(1)',
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.5rem',
                padding: '1rem 1.5rem',
                borderTop: '1px solid #e5e7eb',
              }}
            >
              <button
                onClick={() => setShowModal(false)}
                className="btn btn-secondary"
                data-testid="cancel-crew-button"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="btn btn-primary"
                data-testid="save-crew-button"
              >
                {editing ? 'Save Changes' : 'Create Crew'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Crews;
